// © Microsoft Corporation. All rights reserved.
import { FluentButton, FluentTreeView } from "@fluentui/web-components";
import { closeButtonGlyph, hamburgerMenuGlyph, microsoftLogo } from "../svg";
import { html, ref, repeat, when } from "@microsoft/fast-element";
import { MsftHamburgerMenuItem } from "./hamburger-menu-item";
import { telemetry } from "../utilities/telemetry";
// Prevent tree shaking
FluentButton;
FluentTreeView;
MsftHamburgerMenuItem;
/** Template for a child navigation item */
const childNavigationItemTemplate = html `
    <msft-hamburger-menu-item
        id=${x => x.id}
        nested
        ${telemetry(x => x.telemetryMetadata ? JSON.stringify(x.telemetryMetadata) : null)}
    >
        ${x => x.display}
        ${when(x => x.glyph, html `
                <div slot="start" :innerHTML=${x => x.glyph}></div>
            `)}
    </msft-hamburger-menu-item>
`;
/** Template for navigation items */
const navigationItemTemplate = html `
    <msft-hamburger-menu-item
        id=${x => x.id}
        ${telemetry(x => x.telemetryMetadata ? JSON.stringify(x.telemetryMetadata) : null)}
    >
        ${x => x.display}
        ${when(x => x.glyph, html `
                <div slot="start" :innerHTML=${x => x.glyph}></div>
            `)}
        ${when(x => x.childItems, html `
                ${repeat(x => x.childItems || [], childNavigationItemTemplate)}
            `)}
    </msft-hamburger-menu-item>
    ${when(x => x.showDivider, html ` <fluent-divider class="separator" role="presentation"></fluent-divider> `)}
`;
/** Template for hamburger menu header in desktop */
const menuHeader = html `
    <div class="menu-header">
        <slot name="logo">
            <span class="logo">
                ${microsoftLogo}
            </span>
        </slot>
        <fluent-button
            appearance="stealth"
            class="close-button"
            @click="${x => x.closeMenu()}"
            aria-label="${x => x.closeButtonTooltip}"
            title="${x => x.closeButtonTooltip}"
            tabindex="0"
            ${telemetry(x => x.closeButtonTelemetryTag)}
            ${ref("closeButton")}
        >
            ${closeButtonGlyph}
        </fluent-button>
    </div>
`;
/** Template for hamburger menu in mobile */
const menuHeaderMobile = html `
    <div class="menu-header">
        <span class="mobile-header">
            <slot name="mobile-header"></slot>
        </span>
    </div>
    <fluent-divider class="separator" role="presentation"></fluent-divider>
`;
export const HamburgerMenuTemplate = html `
    <template role="navigation">
        <fluent-button
            appearance="stealth"
            class="${x => (!x.isMenuVisible ? "menu-button" : "hidden")}"
            @click="${x => x.openMenu()}"
            aria-label="${x => x.menuButtonTooltip}"
            title="${x => x.menuButtonTooltip}"
            ${telemetry(x => x.hamburgerMenuButtonTelemetryTag)}
        >
            ${hamburgerMenuGlyph}
        </fluent-button>
        <div
            class="${x => (x.isMenuVisible ? "overlay" : "hidden")} ${x => x.useMobileStyles ? "mobile" : ""}"
            ${ref("menuOverlay")}
        >
            <div
                class="menu"
                @focusout="${(x, c) => x.onMenuFocusout(c.event)}"
                ${ref("menuRef")}
            >
                ${when(x => x.useMobileStyles, menuHeaderMobile)}
                ${when(x => !x.useMobileStyles, menuHeader)}
                <div class="menu-content">
                    <fluent-tree-view
                        @selected-change="${(x, c) => x.onSelectedChange(c.event)}"
                        ${ref("treeView")}
                    >
                        ${repeat(x => x.navItems, navigationItemTemplate)}
                    </fluent-tree-view>
                </div>
            </div>
        </div>
    </template>
`;
